import React, { useState, useEffect } from 'react';
import TrialCountdown from '../components/TrialCountdown';
import { 
  Users, 
  MessageCircle, 
  ArrowRight, 
  ArrowLeft, 
  CheckCircle, 
  Clock, 
  Heart, 
  Brain,
  Sparkles,
  Star,
  Zap,
  Target,
  Shield,
  Activity,
  BarChart3,
  TrendingUp,
  Lightbulb,
  Eye,
  EyeOff,
  Search,
  Filter,
  SortAsc,
  SortDesc,
  Plus,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronRight,
  Download,
  Share,
  Bookmark,
  Calendar,
  Headphones,
  Mic,
  MicOff,
  Wifi,
  WifiOff,
  Globe,
  Smartphone,
  User,
  ArrowRightLeft,
  ExternalLink
} from 'lucide-react';
import api from '../utils/api';

const TherapistHandover = () => {
  const [downloading, setDownloading] = useState(false);

  const therapistResources = [
    {
      id: 1,
      name: 'BetterHelp',
      description: 'Online therapy platform with licensed therapists',
      category: 'Online Therapy',
      website: 'https://www.betterhelp.com',
      app: 'Available on iOS and Android',
      features: ['Video sessions', 'Messaging', 'Group therapy', 'Specialized therapists'],
      icon: Globe,
      rating: 4.8,
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 2,
      name: 'Talkspace',
      description: 'Professional therapy from the comfort of your home',
      category: 'Online Therapy',
      website: 'https://www.talkspace.com',
      app: 'Available on iOS and Android',
      features: ['Unlimited messaging', 'Video sessions', 'Psychiatry', 'Couples therapy'],
      icon: Smartphone,
      rating: 4.7,
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 3,
      name: 'Psychology Today',
      description: 'Find local therapists and mental health professionals',
      category: 'Directory',
      website: 'https://www.psychologytoday.com/us/therapists',
      app: 'Web-based directory',
      features: ['Local therapist search', 'Specialty filters', 'Insurance information', 'Reviews'],
      icon: User,
      rating: 4.6,
      color: 'from-green-500 to-green-600'
    },
    {
      id: 4,
      name: 'GoodTherapy',
      description: 'Find therapists, psychiatrists, and treatment centers',
      category: 'Directory',
      website: 'https://www.goodtherapy.org',
      app: 'Web-based directory',
      features: ['Therapist profiles', 'Treatment approaches', 'Insurance info', 'Articles'],
      icon: Globe,
      rating: 4.5,
      color: 'from-teal-500 to-teal-600'
    },
    {
      id: 5,
      name: '7 Cups',
      description: 'Free online therapy and counseling',
      category: 'Online Therapy',
      website: 'https://www.7cups.com',
      app: 'Available on iOS and Android',
      features: ['Free chat support', 'Licensed therapists', 'Peer support', 'Self-help tools'],
      icon: Smartphone,
      rating: 4.4,
      color: 'from-pink-500 to-pink-600'
    },
    {
      id: 6,
      name: 'Zocdoc',
      description: 'Find and book appointments with therapists',
      category: 'Booking Platform',
      website: 'https://www.zocdoc.com',
      app: 'Available on iOS and Android',
      features: ['Appointment booking', 'Insurance verification', 'Reviews', 'Video visits'],
      icon: Globe,
      rating: 4.3,
      color: 'from-orange-500 to-orange-600'
    }
  ];

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await api.get('/user/export-pdf', { responseType: 'blob' });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sabiwabee-data-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Trial Countdown */}
      <TrialCountdown />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 to-secondary-500/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full mb-6 shadow-lg">
              <ArrowRightLeft className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Therapist Handover
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Seamlessly transition to professional therapy with our curated resources and data export tools
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Verified Platforms</p>
                <p className="text-2xl font-bold text-gray-900">6+</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Licensed Therapists</p>
                <p className="text-2xl font-bold text-gray-900">1000+</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">24/7 Support</p>
                <p className="text-2xl font-bold text-gray-900">Available</p>
              </div>
            </div>
          </div>
        </div>

        {/* Resources Grid */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Professional Therapy Resources</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover trusted platforms and directories to connect with licensed mental health professionals
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {therapistResources.map((resource) => {
              const IconComponent = resource.icon;
              return (
                <div key={resource.id} className="group relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-100 to-gray-200 rounded-2xl transform group-hover:scale-105 transition-transform duration-300"></div>
                  <div className="relative bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 h-full">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center space-x-3">
                        <div className={`w-12 h-12 bg-gradient-to-r ${resource.color} rounded-xl flex items-center justify-center shadow-lg`}>
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{resource.name}</h3>
                          <div className="flex items-center space-x-2 mt-1">
                            <div className="flex items-center">
                              <Star className="w-4 h-4 text-yellow-400 fill-current" />
                              <span className="text-sm font-medium text-gray-700 ml-1">{resource.rating}</span>
                            </div>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                              {resource.category}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 mb-6 leading-relaxed">{resource.description}</p>
                    
                    {/* Platform Info */}
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center space-x-3 text-sm text-gray-600">
                        <Globe className="w-4 h-4 text-primary-500" />
                        <span>Website Available</span>
                      </div>
                      <div className="flex items-center space-x-3 text-sm text-gray-600">
                        <Smartphone className="w-4 h-4 text-primary-500" />
                        <span>{resource.app}</span>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Key Features:</h4>
                      <div className="grid grid-cols-1 gap-2">
                        {resource.features.map((feature, index) => (
                          <div key={index} className="flex items-center space-x-2 text-sm text-gray-600">
                            <div className="w-1.5 h-1.5 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex-shrink-0"></div>
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Action Button */}
                    <div className="mt-auto">
                      <a
                        href={resource.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-semibold py-3 px-6 rounded-xl flex items-center justify-center space-x-2 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>Visit Platform</span>
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Data Export Section */}
        <div className="mb-12">
          <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-3xl p-8 border border-primary-100">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center shadow-lg">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Export Your Therapy Data</h3>
                <p className="text-gray-600">Share your progress with your new therapist</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Download a comprehensive PDF containing all your therapy data including saved chats, AI insights, 
                  journal entries, and assessments. This can be shared with your new therapist to ensure continuity of care.
                </p>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    <span>Chat history and AI conversations</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    <span>Journal entries and mood tracking</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    <span>Assessment results and progress</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    <span>Personalized insights and recommendations</span>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <button 
                  onClick={handleDownloadPDF}
                  disabled={downloading}
                  className="bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 disabled:opacity-50 text-white font-semibold py-4 px-8 rounded-2xl flex items-center justify-center space-x-3 mx-auto transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  <Download className="w-5 h-5" />
                  <span className="text-lg">
                    {downloading ? 'Generating PDF...' : 'Download Therapy Data'}
                  </span>
                </button>
                <p className="text-sm text-gray-500 mt-3">PDF will be generated with your complete therapy history</p>
              </div>
            </div>
          </div>
        </div>

        {/* Important Disclaimer */}
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-3xl p-8 border border-yellow-200">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-2xl flex items-center justify-center shadow-lg">
                <Heart className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold text-yellow-800 mb-3">Important Disclaimer</h3>
              <div className="text-yellow-700 space-y-3">
                <p className="leading-relaxed">
                  Sabiwabee is not a substitute for professional therapy. If you're experiencing a mental health crisis, 
                  please call <strong>988</strong> (US) or your local crisis hotline immediately.
                </p>
                <p className="leading-relaxed">
                  The resources listed above are for informational purposes only and do not constitute medical advice. 
                  Always consult with a licensed mental health professional for personalized treatment.
                </p>
                <div className="bg-yellow-100 rounded-xl p-4 mt-4">
                  <p className="text-sm font-medium text-yellow-800">
                    💡 <strong>Tip:</strong> When choosing a therapist, consider factors like specialization, 
                    insurance coverage, location, and personal comfort level.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TherapistHandover; 